import React, { useContext } from 'react';
import { 
  FormControl, 
  Select, 
  MenuItem, 
  InputLabel,
  CircularProgress,
  Box,
  Typography,
  SelectChangeEvent,
  Tooltip
} from '@mui/material';
import { AnalyticProfileContext, AnalyticProfileContextType } from '../contexts/AnalyticProfileContext';

const AnalyticProfileSelector: React.FC = () => {
  const { 
    availableProfiles, 
    selectedProfileId, 
    isLoading, 
    error, 
    selectProfile 
  } = useContext<AnalyticProfileContextType>(AnalyticProfileContext);

  const handleChange = (event: SelectChangeEvent<string>) => {
    selectProfile(event.target.value);
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center">
        <Typography variant="body2" sx={{ mr: 1, color: '#ddd' }}>Loading profiles</Typography>
        <CircularProgress size={20} sx={{ color: '#ddd' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Tooltip title={error}>
        <Typography variant="body2" sx={{ color: '#ff6b6b' }}>
          Error loading profiles
        </Typography>
      </Tooltip>
    );
  }

  const selectedProfile = availableProfiles.find((p) => p.id === selectedProfileId);

  return (
    <FormControl
      sx={{
        width: 240, // Fixed width for the select
        '& .MuiSelect-select': {
          // Reserve space for the dropdown arrow so truncated text never reaches it
          paddingRight: '36px !important',
          display: 'block',
          minWidth: 0,
        },
        '& .MuiInputLabel-root': {
          color: '#ddd'
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#667eea'
        }
      }}
      size="small"
    >
      <InputLabel id="analytic-profile-select-label" sx={{ color: '#ddd' }}>Analytic Profile</InputLabel>
      <Select
        labelId="analytic-profile-select-label"
        id="analytic-profile-select"
        value={selectedProfileId || ''}
        label="Analytic Profile"
        onChange={handleChange}
        displayEmpty={!selectedProfileId}
        renderValue={() => (
          <Box
            component="span"
            sx={{
              display: 'block',
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {selectedProfile?.name ?? ''}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 320 // Make the dropdown menu wider than the select button
            }
          }
        }}
      >
        {availableProfiles.length === 0 ? (
          <MenuItem disabled value="">
            No profiles available
          </MenuItem>
        ) : (
          availableProfiles.map((profile) => (
            <MenuItem key={profile.id} value={profile.id}>
              <Typography
                sx={{
                  width: '100%'
                }}
              >
                {profile.name}
              </Typography>
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default AnalyticProfileSelector; 